/*
 * Real mastering toolkit — pure Web Audio.
 *  - measureLufs(buffer):     ITU-R BS.1770 K-weighted integrated LUFS
 *  - measureTruePeak(buffer): 4x oversampled peak (approximates ITU-R BS.1770-4 true peak)
 *  - measureSpectralTilt(buffer): low/mid/high band energy in dB
 *  - buildMasteringChain(ctx, input, output, params): inserts EQ + comp + limiter + makeup
 *
 * The chain is graph-stable: every node is created up-front; toggling "off" means
 * setting parameters to identity (0 dB gain, 1:1 ratio, threshold 0). No reconnects.
 */

const KWEIGHT_HIGHPASS_HZ = 38;
const KWEIGHT_HIGHSHELF_HZ = 1681;
const KWEIGHT_HIGHSHELF_GAIN_DB = 4;

/** Render `buffer` through ITU-R BS.1770 K-weighting filters and return the K-weighted buffer. */
async function kWeight(buffer) {
  const offline = new OfflineAudioContext(
    Math.max(1, buffer.numberOfChannels),
    buffer.length,
    buffer.sampleRate,
  );
  const src = offline.createBufferSource();
  src.buffer = buffer;

  // Stage 1: pre-filter — high-pass (Butterworth-ish via biquad)
  const hp = offline.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = KWEIGHT_HIGHPASS_HZ;
  hp.Q.value = 0.5;

  // Stage 2: revised low frequency B-curve — high-shelf @ 1681 Hz, +4 dB
  const hs = offline.createBiquadFilter();
  hs.type = "highshelf";
  hs.frequency.value = KWEIGHT_HIGHSHELF_HZ;
  hs.gain.value = KWEIGHT_HIGHSHELF_GAIN_DB;

  src.connect(hp);
  hp.connect(hs);
  hs.connect(offline.destination);
  src.start(0);
  return offline.startRendering();
}

function meanSquare(buffer) {
  let sum = 0;
  let count = 0;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < data.length; i++) {
      sum += data[i] * data[i];
    }
    count += data.length;
  }
  if (count === 0) return 0;
  // For stereo, BS.1770 sums channels — here we average over total samples which is
  // mathematically equivalent for an integrated measurement when channel gains are equal.
  return sum / count;
}

/** Returns integrated LUFS for the entire buffer. Silent buffer → -Infinity (we cap at -70). */
export async function measureLufs(buffer) {
  const k = await kWeight(buffer);
  const ms = meanSquare(k);
  if (ms <= 1e-12) return -70;
  const lufs = -0.691 + 10 * Math.log10(ms);
  return lufs;
}

/** Approximates ITU-R BS.1770-4 true-peak with 4x linear oversampling. Returns dBTP. */
export function measureTruePeak(buffer) {
  let peak = 0;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < data.length - 1; i++) {
      const a = data[i];
      const b = data[i + 1];
      // 4x linear interpolation between samples
      for (let f = 0; f < 4; f++) {
        const v = a + (b - a) * (f / 4);
        const abs = Math.abs(v);
        if (abs > peak) peak = abs;
      }
    }
    // last sample
    const tail = Math.abs(data[data.length - 1]);
    if (tail > peak) peak = tail;
  }
  if (peak <= 1e-9) return -120;
  return 20 * Math.log10(peak);
}

/** Splits the mix into low (<250 Hz), mid (250-4 kHz), high (>4 kHz) bands and returns
 *  their RMS in dBFS. Useful for telling the user the spectral tilt of their mix. */
export async function measureSpectralTilt(buffer) {
  const measureBand = async (type, freq, q) => {
    const offline = new OfflineAudioContext(buffer.numberOfChannels, buffer.length, buffer.sampleRate);
    const src = offline.createBufferSource();
    src.buffer = buffer;
    const f = offline.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    src.connect(f);
    f.connect(offline.destination);
    src.start(0);
    const rendered = await offline.startRendering();
    const ms = meanSquare(rendered);
    return ms <= 1e-12 ? -120 : 10 * Math.log10(ms);
  };

  const [low, mid, high] = await Promise.all([
    measureBand("lowpass",  250,  0.707),
    measureBand("bandpass", 1000, 1.0),
    measureBand("highpass", 4000, 0.707),
  ]);
  return { low, mid, high };
}

/** Target presets — integrated LUFS targets per delivery context. */
export const MASTERING_TARGETS = {
  streaming: { label: "Streaming",       lufs: -14, ceiling: -1.0 },
  album:     { label: "Album / Apple",   lufs: -16, ceiling: -1.0 },
  loud:      { label: "Loud / Club",     lufs:  -9, ceiling: -0.3 },
  podcast:   { label: "Podcast / Voice", lufs: -19, ceiling: -1.0 },
};

/**
 * Build a real mastering chain in any AudioContext. Returns { input, output, apply(params), nodes }.
 *
 * Chain: input → eqLow(lowshelf) → eqMid(peaking) → eqHigh(highshelf)
 *              → compressor (broadband, slow attack) → limiter (transient catch)
 *              → makeupGain → output
 *
 * params (all optional):
 *   eqLowGainDb, eqMidGainDb, eqHighGainDb  — corrective tilt (default 0)
 *   compThresholdDb (default -16), compRatio (default 2.5)
 *   ceilingDb (default -1)
 *   makeupGainDb (default 0)
 */
export function buildMasteringChain(ctx, input, output) {
  const eqLow = ctx.createBiquadFilter();
  eqLow.type = "lowshelf";  eqLow.frequency.value = 120; eqLow.gain.value = 0;

  const eqMid = ctx.createBiquadFilter();
  eqMid.type = "peaking";   eqMid.frequency.value = 1500; eqMid.Q.value = 0.7; eqMid.gain.value = 0;

  const eqHigh = ctx.createBiquadFilter();
  eqHigh.type = "highshelf"; eqHigh.frequency.value = 8000; eqHigh.gain.value = 0;

  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = 0; compressor.knee.value = 12; compressor.ratio.value = 1;
  compressor.attack.value = 0.025; compressor.release.value = 0.25;

  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = 0; limiter.knee.value = 0; limiter.ratio.value = 1;
  limiter.attack.value = 0.001; limiter.release.value = 0.05;

  const makeupGain = ctx.createGain();
  makeupGain.gain.value = 1;

  input.connect(eqLow);
  eqLow.connect(eqMid);
  eqMid.connect(eqHigh);
  eqHigh.connect(compressor);
  compressor.connect(limiter);
  limiter.connect(makeupGain);
  makeupGain.connect(output);

  const nodes = { eqLow, eqMid, eqHigh, compressor, limiter, makeupGain };

  function apply(params = {}) {
    const t = ctx.currentTime;
    const tau = 0.05;
    eqLow.gain.setTargetAtTime(params.eqLowGainDb ?? 0, t, tau);
    eqMid.gain.setTargetAtTime(params.eqMidGainDb ?? 0, t, tau);
    eqHigh.gain.setTargetAtTime(params.eqHighGainDb ?? 0, t, tau);

    if (params.compThresholdDb != null) {
      compressor.threshold.setTargetAtTime(params.compThresholdDb, t, tau);
      compressor.ratio.setTargetAtTime(params.compRatio ?? 2.5, t, tau);
    } else {
      compressor.threshold.setTargetAtTime(0, t, tau);
      compressor.ratio.setTargetAtTime(1, t, tau);
    }

    if (params.ceilingDb != null) {
      limiter.threshold.setTargetAtTime(params.ceilingDb, t, tau);
      limiter.ratio.setTargetAtTime(20, t, tau);
    } else {
      limiter.threshold.setTargetAtTime(0, t, tau);
      limiter.ratio.setTargetAtTime(1, t, tau);
    }

    const makeupLinear = Math.pow(10, (params.makeupGainDb ?? 0) / 20);
    makeupGain.gain.setTargetAtTime(makeupLinear, t, tau);
  }

  function reset() { apply({}); }

  return { input: eqLow, output: makeupGain, apply, reset, nodes };
}

/**
 * Suggest a mastering recipe given the current mix's LUFS, true-peak and spectral tilt
 * and a target preset. Returns the params object that buildMasteringChain.apply() accepts.
 */
export function suggestRecipe({ currentLufs, currentTruePeakDb, tilt, target }) {
  const tgt = typeof target === "string" ? MASTERING_TARGETS[target] : target;
  if (!tgt) throw new Error("Unknown mastering target");

  // 1. Corrective tilt — gently reduce whichever band is dominant
  const bands = [tilt.low, tilt.mid, tilt.high];
  const avg = (bands[0] + bands[1] + bands[2]) / 3;
  const excessDb = (v) => Math.max(0, v - avg);
  const eqLowGainDb  = -Math.min(2.5, excessDb(tilt.low)  * 0.4);
  const eqMidGainDb  = -Math.min(2.0, excessDb(tilt.mid)  * 0.4);
  const eqHighGainDb = -Math.min(2.5, excessDb(tilt.high) * 0.4);

  // 2. Compression depth — proportional to how much LUFS lift is needed
  const lufsDelta = tgt.lufs - currentLufs; // positive => need to be louder
  const compThresholdDb = -Math.min(20, Math.max(8, 12 + lufsDelta * 0.5));
  const compRatio = lufsDelta > 8 ? 3.5 : lufsDelta > 4 ? 2.8 : 2.0;

  // 3. Final limiter ceiling from target
  const ceilingDb = tgt.ceiling;

  // 4. Make-up gain — fill the rest of the LUFS delta after EQ + comp do their part.
  //    Cap so we never crank silent mixes to 11.
  const makeupGainDb = Math.max(0, Math.min(12, lufsDelta - 2));

  return {
    target: tgt,
    params: { eqLowGainDb, eqMidGainDb, eqHighGainDb, compThresholdDb, compRatio, ceilingDb, makeupGainDb },
    notes: {
      lufsDelta: Math.round(lufsDelta * 10) / 10,
      truePeakBeforeDb: Math.round(currentTruePeakDb * 10) / 10,
    },
  };
}
